import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiErr, mediaUrl } from "@/lib/api";
import { GoldButton, Section } from "@/components/site/Ui";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import { useSite } from "@/context/SiteContext";
import { getRecaptchaToken } from "@/lib/recaptcha";
import { toast } from "sonner";
import { FileDown, Wrench, Sparkles, CheckCircle2 } from "lucide-react";
import VideoIdeaGenerator from "@/components/site/VideoIdeaGenerator";

function ResourceCard({ res }) {
  const { settings } = useSite();
  const [form, setForm] = useState({ name: "", email: "", consent: false });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const recaptcha_token = await getRecaptchaToken(settings.recaptcha_site_key, "newsletter");
      const { data } = await api.post("/subscribe", { ...form, resource_slug: res.slug, recaptcha_token });
      setDone(true);
      if (data.download_ready && data.download_url) {
        window.open(mediaUrl(data.download_url), "_blank");
        toast.success("Downloading now — check your email too!");
      } else {
        toast.success("You're on the list! We'll email the guide shortly.");
      }
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || "Something went wrong.");
    } finally { setLoading(false); }
  };

  return (
    <div className="card-surface rounded-2xl p-7 h-full flex flex-col gold-glow-hover">
      <div className="grid place-items-center h-12 w-12 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30">
        <FileDown className="text-[#D4AF37]" strokeWidth={1.5} size={22} />
      </div>
      <h3 className="mt-5 text-xl font-display">{res.title}</h3>
      <p className="mt-2 text-sm text-white/60 leading-relaxed flex-1">{res.description}</p>
      {done ? (
        <div className="mt-6 flex items-center gap-2 text-[#D4AF37] text-sm"><CheckCircle2 size={18} /> Sent! Check your email{res.has_pdf ? " (and your downloads)" : ""}.</div>
      ) : (
        <form onSubmit={submit} className="mt-6 space-y-3" data-testid={`resource-form-${res.slug}`}>
          <input required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid={`resource-name-${res.slug}`}
            className="w-full rounded-lg bg-black/40 border border-white/10 px-3 py-2.5 text-sm outline-none focus:border-[#D4AF37]" />
          <input required type="email" placeholder="Your email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid={`resource-email-${res.slug}`}
            className="w-full rounded-lg bg-black/40 border border-white/10 px-3 py-2.5 text-sm outline-none focus:border-[#D4AF37]" />
          <label className="flex items-start gap-2 text-xs text-white/55">
            <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-0.5 accent-[#D4AF37]" />
            Yes, send me helpful tips and updates by email. (Optional)
          </label>
          <GoldButton type="submit" disabled={loading} data-testid={`resource-submit-${res.slug}`} className="w-full">
            {loading ? "Sending..." : "Get it free"}
          </GoldButton>
        </form>
      )}
    </div>
  );
}

export default function FreeResources() {
  const [resources, setResources] = useState([]);
  useEffect(() => { api.get("/resources").then((r) => setResources(r.data)).catch(() => {}); }, []);

  return (
    <>
      <div className="relative grain overflow-hidden">
        <Section className="pb-8">
          <Reveal>
            <Eyebrow>Free Resources</Eyebrow>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl max-w-3xl leading-[1.05]">Free tools to help you start today.</h1>
            <p className="mt-5 text-white/70 max-w-xl text-lg">Grab these free guides — no cost, no catch. Just enter your name and email and they're yours.</p>
          </Reveal>
        </Section>
      </div>

      <Section className="pt-4">
        <div className="grid md:grid-cols-2 gap-6">
          {resources.map((r, i) => (
            <Reveal key={r.id} delay={i * 0.08}><ResourceCard res={r} /></Reveal>
          ))}
        </div>

        <div className="mt-16"><VideoIdeaGenerator /></div>

        <Reveal>
          <div className="mt-16 rounded-3xl border border-[#D4AF37]/25 p-8 sm:p-10 grain relative overflow-hidden">
            <div className="absolute inset-0 -z-10 bg-gradient-to-br from-[#D4AF37]/10 to-transparent" />
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 justify-between">
              <div>
                <div className="flex items-center gap-2 text-[#D4AF37]"><Wrench size={20} /><span className="text-xs uppercase tracking-widest">Recommended Gear</span></div>
                <h2 className="mt-3 font-display text-2xl sm:text-3xl">The beginner kit I actually recommend</h2>
                <p className="mt-2 text-white/60 max-w-lg">Affordable phone accessories that make a real difference — with a video where I explain each one.</p>
              </div>
              <GoldButton as="link" to="/gear">See the gear</GoldButton>
            </div>
          </div>
        </Reveal>

        <Reveal>
          <div className="mt-10 flex items-center gap-3 text-white/40 text-sm">
            <Sparkles size={16} className="text-[#D4AF37]" />
            More free tools coming soon — a Thumbnail Checklist, Hook Writer and more.
          </div>
        </Reveal>
      </Section>
    </>
  );
}
